import { useCallback, useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { ChipList } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { FILTERS } from "../../constants/filters"
import { useRoadworksDispatchContext, useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { useGetProjectTechniqueCodes } from "../../hooks/queries/useGetProjectTechniqueCodes"
import { roadworksFiltersConfig } from "../../utils/roadworksFiltersConfig"

export function TechniquesFilterList(props: TechniquesFilterListProps) {

    const {
        enabledFilters = FILTERS,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { filters } = useRoadworksStateContext()
    const { applyFilters } = useRoadworksDispatchContext()

    const { data: techniqueCodes, isLoading: isLoadingTechniques, isLoading: isErrorTechniques } = useGetProjectTechniqueCodes()

    const displayedFilters = useMemo(() => {
        if (filters && techniqueCodes) {
            const config = {
                techniqueCodes: techniqueCodes,
                t: t,
                namespace: I18N_NAMESPACE,
            }
            return Object.entries(filters).map(([filterKey, filterValues]) => {
                if (!enabledFilters?.includes(filterKey)) {
                    return null
                }
                const key = filterKey as keyof typeof roadworksFiltersConfig
                return roadworksFiltersConfig[key].getValueList(key, filterValues, config)
            }).flat().filter(Boolean)
        }
        return null
    }, [filters, techniqueCodes, enabledFilters, t])

    const deleteFilter = useCallback((filter) => {
        if (!displayedFilters) return
        if (!filter) return
        const filterKey = filter.key as keyof typeof roadworksFiltersConfig
        const filterValue = [...filters[filterKey as keyof typeof filters]]
        const newFilterValue = roadworksFiltersConfig[filterKey].removeValue(filterValue, filter)
        applyFilters({
            ...filters,
            [filterKey]: newFilterValue,
        })
    }, [displayedFilters, filters, applyFilters])

    if (displayedFilters && !isLoadingTechniques && !isErrorTechniques) {
        return <ChipList
            {...otherProps}
            onDelete={deleteFilter}
            values={displayedFilters}
            variant="filled"
        />
    }
    return null
}

type TechniquesFilterListProps = {
    enabledFilters?: Array<typeof FILTERS[number]>
}